import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Sparkles } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { setDrawerState } from '../dashboardSlice';
import { MultiAskAIContext } from '../index';

interface MultiAskAIProps {
  open: boolean;
}

const MultiAskAI: React.FC<MultiAskAIProps> = ({ open }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setShowMultiAskAI } = React.useContext(MultiAskAIContext);

  // Check if the current route is the MultiAskAI page
  const isActive = location.pathname === '/multi-ask-ai';

  const handleMultiAskAIClick = () => {
    // Close drawer if on mobile
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }

    // Set the MultiAskAI context to show
    setShowMultiAskAI(true);

    // Navigate to MultiAskAI page
    navigate('/multi-ask-ai');
  };

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Tooltip title={t('drawer.multiAskAI.tooltip')} placement="right" arrow>
        <ListItemButton
          onClick={handleMultiAskAIClick}
          sx={{
            justifyContent: open ? 'initial' : 'center',
            px: open ? 2.5 : 1.25,
            borderRadius: 20,
            mx: 0.5,
            minHeight: '36px',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'transparent',
            '&.Mui-selected': {
              bgcolor: 'transparent',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: isActive
                ? theme.palette.action.hover
                : 'secondary.light',
              opacity: isActive ? 1 : 0,
              borderRadius: 'inherit',
            },
            '&:hover': {
              '&::before': {
                opacity: 1,
              },
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
              pl: open ? 0 : 0.5,
              color: isActive ? 'inherit' : 'text.secondary',
              transition: theme.transitions.create(
                ['margin', 'padding', 'color'],
                {
                  duration: 150,
                  easing: theme.transitions.easing.easeOut,
                }
              ),
              '& svg': {
                color: 'inherit',
              },
            }}
          >
            <Sparkles />
          </ListItemIcon>
          <ListItemText
            primary={t('drawer.multiAskAI.title')}
            sx={{
              opacity: open ? 1 : 0,
              transition: theme.transitions.create(['opacity'], {
                duration: open ? 150 : 100,
                easing: theme.transitions.easing.easeOut,
                delay: open ? 50 : 0,
              }),
              '& .MuiTypography-root': {
                fontWeight: isActive ? 700 : 400,
                color: isActive ? 'text.primary' : 'text.secondary',
              },
            }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default MultiAskAI;
